'use client';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScrollAnimation from '@/components/ScrollAnimation';
import { ShieldAlert, Database, KeyRound, ArrowRight, ExternalLink, ShieldCheck } from 'lucide-react';
import Link from 'next/link';
import { softwareApplicationSchema, breadcrumbSchema } from '@/lib/utils/structured-data';
import { SITE_URL } from '@/lib/site';

const incidents = [
  {
    icon: Database,
    title: 'A social app, breached within days of launch',
    body: 'A vibe-coded social network launched fast and was breached almost immediately - roughly 1.5M API auth tokens and 35k email addresses exposed through a Supabase database shipped with Row-Level Security disabled. No sophisticated attacker required.',
    source: 'Moltbook breach, 2026',
    href: 'https://thenextweb.com/news/lovable-vibe-coding-security-crisis-exposed',
  },
  {
    icon: KeyRound,
    title: 'API keys sitting in the browser bundle',
    body: 'In an audit of live AI-built apps, ~60% of Lovable apps exposed an API key directly in the client bundle, and over half had no authentication on sensitive routes - the default output of the tool, shipped straight to production.',
    source: 'vibeAudit research, 2026',
    href: 'https://vibeaudit.net/research',
  },
  {
    icon: ShieldAlert,
    title: '“This is not hacking. This is five API calls.”',
    body: 'A researcher found every Lovable project created before a certain date had been exposed for 48 days - any free account could read the source code, database credentials and customer data of other projects. The exploit was trivial.',
    source: 'Lovable security disclosure, 2026',
    href: 'https://www.theregister.com/2026/02/27/lovable_app_vulnerabilities/',
  },
];

const steps = [
  { n: '01', t: 'AI generates the app', d: 'The tool ships working UI fast - and skips secure defaults. RLS off, keys in the bundle, no auth on routes.' },
  { n: '02', t: 'It goes live', d: 'The app is deployed publicly. The database is one HTTP call away from the internet.' },
  { n: '03', t: 'Someone pokes it', d: 'A curious user (or a bot) tries the anon key against the database, or calls an API route without a token.' },
  { n: '04', t: 'Data walks out', d: 'Records, credentials and PII are read or exported. The founder finds out from a tweet - or a breach report.' },
];

const caseStudyBreadcrumb = breadcrumbSchema([
  { name: 'Home', url: SITE_URL },
  { name: 'AI Security Incidents', url: `${SITE_URL}/case-study` },
]);

export default function CaseStudyPage() {
  return (
    <div className="min-h-screen bg-black">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareApplicationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(caseStudyBreadcrumb) }} />
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-neutral-900/20 via-black to-black"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]"></div>
      </div>

      <Header />
      <ScrollAnimation />

      <main>
        <section className="relative pt-32 pb-24">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-16" data-scroll-animate="fade-up">
              <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-6">
                <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700"></div>
                Anatomy of a breach
                <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700"></div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-5 text-white">
                How AI-built apps get breached
              </h1>
              <p className="text-lg text-neutral-400 max-w-2xl mx-auto leading-relaxed">
                We don&apos;t have customer case studies yet - we&apos;re early, and we won&apos;t invent them. Instead,
                here are real, documented public incidents that show exactly the failure mode Silker is built to stop.
              </p>
            </div>

            {/* The 4-step pattern */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-20" data-scroll-animate="fade-up">
              {steps.map((s) => (
                <div key={s.n} className="bg-neutral-950 p-6">
                  <div className="text-xs font-mono font-bold text-rose-400 mb-3">{s.n}</div>
                  <h3 className="text-white font-medium mb-2">{s.t}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>

            {/* Real incidents */}
            <h2 className="text-2xl font-semibold text-white mb-8 text-center" data-scroll-animate="fade-up">
              Documented public incidents
            </h2>
            <div className="space-y-px bg-white/10 border border-white/10 mb-20" data-scroll-animate="fade-up">
              {incidents.map((inc, i) => {
                const Icon = inc.icon;
                return (
                  <div key={i} className="bg-neutral-950 p-6 sm:p-8 flex items-start gap-5">
                    <div className="p-2.5 border border-rose-500/30 bg-rose-500/5 shrink-0">
                      <Icon className="w-5 h-5 text-rose-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{inc.title}</h3>
                      <p className="text-sm text-neutral-400 leading-relaxed mb-3">{inc.body}</p>
                      <a
                        href={inc.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-rose-400 transition-colors font-mono"
                      >
                        {inc.source}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* What Silker does about it */}
            <div className="rounded-none border border-white/10 bg-neutral-900/30 p-6 sm:p-10 mb-16" data-scroll-animate="fade-up">
              <h2 className="text-2xl font-semibold text-white mb-6">Where Silker fits</h2>
              <ul className="space-y-4">
                {[
                  'A free scan tells you, in plain language, if your database is open or your keys are exposed - before you launch.',
                  'The runtime guard (SDK, Cloudflare Worker or Docker proxy) blocks abuse and data leaks live, instead of emailing a report after the fact.',
                  'Continuous monitoring re-checks your app and alerts you when something new breaks.',
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-neutral-300">
                    <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-center" data-scroll-animate="fade-up">
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  href="/#scan"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-black rounded-none text-sm font-semibold hover:bg-neutral-200 transition-colors"
                >
                  Scan your app - free
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="/architecture"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/5 text-white border border-white/10 rounded-none text-sm font-semibold hover:bg-white/10 transition-colors"
                >
                  See how it works
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
